import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { ClockIcon, HeartIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import ItemCardGrid from '@/components/content/item-card-grid';
import HomeBanner from '@/components/home/home-banner';
import WelcomeHeader from '@/components/home/welcome-header';
import QuickActions from '@/components/home/quick-actions';
import ContentCarousel from '@/components/home/content-carousel';
import CarouselEmptyState from '@/components/home/carousel-empty-state';
import ContentTypePickerDialog from '@/components/home/content-type-picker-dialog';
import FindContentDialog from '@/components/home/find-content-dialog';
import ShareContentDialog from '@/components/home/share-content-dialog';
import { FilterOption } from '@/components/home/filter-chips';
import ContentDrawer from '@/pages/contents/content-drawer';
import useWorkspace from '@/lib/hooks/use-workspace';
import useIsCurrentUser from '@/lib/hooks/use-is-current-user';
import useIsViewer from '@/lib/hooks/use-is-viewer';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import useContentTypesStore from '@/utils/use-content-type';
import api from '@/utils/api';
import { buildPublicUrl } from '@/utils/public-url';
import { Content, Favorite } from '@/lib/types';

const DOT_PALETTE = [
  'red.400',
  'green.400',
  'blue.400',
  'purple.400',
  'cyan.400',
  'orange.400',
  'pink.400',
  'teal.400',
];

function pluralize(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return trimmed;
  if (/s$/i.test(trimmed)) return trimmed;
  if (/y$/i.test(trimmed)) return trimmed.replace(/y$/i, 'ies');
  return `${trimmed}s`;
}

function matchesFilter(item: Content, filterValue: string): boolean {
  if (filterValue === 'all') return true;
  return item.content_type_name === filterValue;
}

type PartnerWorkspace = {
  id: number;
  name: string;
};

export default function Home() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const currentUser = useIsCurrentUser();
  const isViewer = useIsViewer();
  const { contentTypes, loadContentTypes } = useContentTypesStore();

  const [favorites, setFavorites] = useState<{ [key: number]: Favorite }>({});
  const [favoriteContents, setFavoriteContents] = useState<Content[]>([]);
  const [recentContents, setRecentContents] = useState<Content[]>([]);
  const [partnerContents, setPartnerContents] = useState<Content[]>([]);
  const [partnerWorkspaces, setPartnerWorkspaces] = useState<PartnerWorkspace[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [isFavoritesLoading, setIsFavoritesLoading] = useState(true);
  const [isRecentLoading, setIsRecentLoading] = useState(true);
  const [isPartnerLoading, setIsPartnerLoading] = useState(true);

  const [favoritesFilter, setFavoritesFilter] = useState('all');
  const [recentFilter, setRecentFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [browseDialogOpen, setBrowseDialogOpen] = useState(false);
  const [findDialogOpen, setFindDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<Content | undefined>();
  const [autoOpenPrintDialog, setAutoOpenPrintDialog] = useState(false);
  const contentDrawer = useModalToggle();

  useEffect(() => {
    if (workspaceId) loadContentTypes(workspaceId);
  }, [workspaceId, loadContentTypes]);

  const fetchFavorites = useCallback(async () => {
    if (!currentUser?.id || !workspaceId) return;
    try {
      const [refsRes, contentsRes] = await Promise.all([
        api.get(`favorites/list/${currentUser.id}`),
        api.get(`favorites/list/${currentUser.id}/workspace/${workspaceId}/contents`),
      ]);
      const refs = (refsRes.data.data || []) as Favorite[];
      const map: { [key: number]: Favorite } = {};
      refs.forEach((fav) => {
        map[fav.workspace_content_id] = fav;
      });
      setFavorites(map);
      setFavoriteContents(contentsRes.data.data || []);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load favorites',
      });
    } finally {
      setIsFavoritesLoading(false);
    }
  }, [currentUser, workspaceId]);

  const fetchRecent = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await api.get(`contents/recent/${workspaceId}?limit=12`);
      setRecentContents(res.data.data || []);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load recent content',
      });
    } finally {
      setIsRecentLoading(false);
    }
  }, [workspaceId]);

  const fetchPartnerWorkspaces = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await api.get(`public-workspace-selections/${workspaceId}`);
      const rows = (res.data.data || []) as Array<{
        public_workspace_id: number;
        public_workspace?: { id?: number; name?: string };
      }>;
      const partners: PartnerWorkspace[] = rows
        .map((row) => ({
          id: row.public_workspace?.id ?? row.public_workspace_id,
          name: row.public_workspace?.name ?? `Partner ${row.public_workspace_id}`,
        }))
        .filter((p) => p.id);
      setPartnerWorkspaces(partners);
      if (partners.length > 0 && !selectedPartnerId) {
        setSelectedPartnerId(String(partners[0].id));
      } else if (partners.length === 0) {
        setSelectedPartnerId('');
        setIsPartnerLoading(false);
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load partner workspaces',
      });
      setIsPartnerLoading(false);
    }
  }, [workspaceId, selectedPartnerId]);

  const fetchPartnerContents = useCallback(async () => {
    if (!workspaceId || !selectedPartnerId) return;
    setIsPartnerLoading(true);
    try {
      const res = await api.get(
        `contents/partner/${workspaceId}?partner_id=${selectedPartnerId}&limit=1000`,
      );
      setPartnerContents(res.data.data || []);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load partner content',
      });
    } finally {
      setIsPartnerLoading(false);
    }
  }, [workspaceId, selectedPartnerId]);

  useEffect(() => {
    fetchFavorites();
    fetchRecent();
    fetchPartnerWorkspaces();
  }, [fetchFavorites, fetchRecent, fetchPartnerWorkspaces]);

  useEffect(() => {
    if (selectedPartnerId) {
      fetchPartnerContents();
      setPartnerFilter('all');
    }
  }, [selectedPartnerId, fetchPartnerContents]);

  const openContentDrawer = async (item: Content, autoPrint: boolean) => {
    const currentWorkspaceId = workspaceId ? parseInt(workspaceId, 10) : NaN;
    if (item.workspace_id === currentWorkspaceId) {
      try {
        const response = await api.get(`public/content/${item.reference_id}`);
        const content = response.data.data;
        if (!content) return;
        setSelectedContent(content);
        setAutoOpenPrintDialog(autoPrint);
        contentDrawer.open();
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to fetch content',
        });
      }
      return;
    }
    if (item.public_link) {
      const url = buildPublicUrl('content', item.reference_id) || item.public_link;
      window.open(url, '_blank', 'noopener');
      return;
    }
    navigate(
      `/${workspaceId}/content-types/${item.content_type_id}/contents/${item.id}/edit`,
    );
  };

  const handleView = async (item: Content) => {
    await openContentDrawer(item, false);
  };

  const handleEdit = (item: Content) => {
    navigate(
      `/${workspaceId}/content-types/${item.content_type_id}/contents/${item.id}/edit`,
    );
  };

  const handleFavourite = async (item: Content) => {
    if (!currentUser?.id) return;
    try {
      if (favorites[item.id]) {
        await api.delete(`favorites/${favorites[item.id].id}`);
        toaster.create({ type: 'success', title: 'Removed from favorites' });
      } else {
        await api.post('favorites', {
          user_id: currentUser.id,
          workspace_content_id: item.id,
        });
        toaster.create({ type: 'success', title: 'Added to favorites' });
      }
      fetchFavorites();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update favorites',
      });
    }
  };

  const handleDuplicate = async (item: Content) => {
    try {
      await api.post(`contents/duplicate/${item.id}`);
      toaster.create({ type: 'success', title: 'Content duplicated' });
      fetchRecent();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to duplicate content',
      });
    }
  };

  const handleDelete = async (item: Content) => {
    try {
      await api.delete(`contents/${item.id}`);
      toaster.create({ type: 'success', title: 'Content removed' });
      fetchRecent();
      fetchFavorites();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete content',
      });
    }
  };

  const handleChangeStatus = async (item: Content, status: 'draft' | 'published') => {
    try {
      await api.patch(`contents/status/${item.id}/${status}`);
      toaster.create({
        type: 'success',
        title: status === 'published' ? 'Shared to workspace' : 'Hidden from workspace',
      });
      fetchRecent();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update status',
      });
    }
  };

  const handlePrint = async (item: Content) => {
    await openContentDrawer(item, true);
  };

  const handleSharePublicLink = async (item: Content) => {
    try {
      let fallback: string | undefined = item.public_link;
      if (!item.public_link) {
        const res = await api.put(`contents/public/${item.id}`);
        fallback = res.data?.data?.public_link;
      }
      const link = buildPublicUrl('content', item.reference_id) || fallback;
      if (link) {
        await navigator.clipboard.writeText(link);
        toaster.create({ type: 'success', title: 'Public link copied to clipboard' });
      }
      fetchRecent();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to share content',
      });
    }
  };

  const filterOptions = useMemo<FilterOption[]>(() => {
    const opts: FilterOption[] = [{ value: 'all', label: 'All' }];
    contentTypes.forEach((ct, i) => {
      opts.push({
        value: ct.name,
        label: pluralize(ct.name),
        dotColor: DOT_PALETTE[i % DOT_PALETTE.length],
      });
    });
    return opts;
  }, [contentTypes]);

  const partnerWorkspaceFilters = useMemo<FilterOption[]>(
    () =>
      partnerWorkspaces.map((p) => ({
        value: String(p.id),
        label: p.name,
      })),
    [partnerWorkspaces],
  );

  const partnerContentTypeFilters = useMemo<FilterOption[]>(() => {
    const seen = new Map<string, number>();
    partnerContents.forEach((c) => {
      const name = c.content_type_name?.trim();
      if (name && !seen.has(name)) {
        seen.set(name, seen.size);
      }
    });
    const opts: FilterOption[] = [{ value: 'all', label: 'All' }];
    for (const [name, idx] of seen) {
      opts.push({
        value: name,
        label: pluralize(name),
        dotColor: DOT_PALETTE[idx % DOT_PALETTE.length],
      });
    }
    return opts;
  }, [partnerContents]);

  const visibleFavorites = useMemo(
    () => favoriteContents.filter((c) => matchesFilter(c, favoritesFilter)),
    [favoriteContents, favoritesFilter],
  );

  const visibleRecent = useMemo(
    () => recentContents.filter((c) => matchesFilter(c, recentFilter)),
    [recentContents, recentFilter],
  );

  const visiblePartner = useMemo(
    () => partnerContents.filter((c) => matchesFilter(c, partnerFilter)),
    [partnerContents, partnerFilter],
  );

  const openBrowseDialog = () => {
    if (contentTypes.length === 0) {
      navigate(`/${workspaceId}/content-types`);
      return;
    }
    setBrowseDialogOpen(true);
  };

  const cardProps = (item: Content) => ({
    item,
    favorites,
    lastContentElementRef: () => undefined,
    onView: handleView,
    onFavourite: handleFavourite,
    onGeneratePiblicLink: handleSharePublicLink,
    onDuplicate: handleDuplicate,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onChangeStatus: handleChangeStatus,
    onPrint: handlePrint,
  });

  return (
    <Box pb={10}>
      <HomeBanner workspace={workspace} />
      <Box mx='auto' width={{ md: '90%' }} maxW='6xl' px={{ base: 4, md: 0 }}>
        <WelcomeHeader firstName={currentUser?.first_name} />
        <QuickActions
          onCreateContent={isViewer ? undefined : () => setCreateDialogOpen(true)}
          onBuildCollection={isViewer ? undefined : () => navigate(`/${workspaceId}/collection-types`)}
          onBrowseContent={openBrowseDialog}
          onFindContent={() => setFindDialogOpen(true)}
          onShareContent={() => setShareDialogOpen(true)}
        />
        <ContentCarousel
          title='Favorites'
          filters={filterOptions}
          filterValue={favoritesFilter}
          onFilterChange={setFavoritesFilter}
          isLoading={isFavoritesLoading}
          emptyState={
            <CarouselEmptyState
              icon={HeartIcon}
              iconBg='red.50'
              iconColor='red.500'
              title='No favorites yet'
              description='Tap the heart on any drill, play, or skill to pin it here for one-tap access on your home page.'
              actionLabel='Browse content'
              onAction={openBrowseDialog}
            />
          }
        >
          {visibleFavorites.map((item) => (
            <ItemCardGrid key={item.id} {...cardProps(item)} />
          ))}
        </ContentCarousel>
        <ContentCarousel
          title='Recently added'
          filters={filterOptions}
          filterValue={recentFilter}
          onFilterChange={setRecentFilter}
          isLoading={isRecentLoading}
          emptyState={
            <CarouselEmptyState
              icon={ClockIcon}
              iconBg='blue.50'
              iconColor='blue.500'
              title='Nothing added yet'
              description='New drills, plays, and articles you or your team create will show up here.'
              actionLabel='Create content'
              onAction={() => setCreateDialogOpen(true)}
            />
          }
        >
          {visibleRecent.map((item) => (
            <ItemCardGrid key={item.id} {...cardProps(item)} />
          ))}
        </ContentCarousel>
        <ContentCarousel
          title='Partner content'
          primaryFilters={partnerWorkspaceFilters.length > 0 ? partnerWorkspaceFilters : undefined}
          primaryFilterValue={selectedPartnerId}
          onPrimaryFilterChange={setSelectedPartnerId}
          filters={partnerContentTypeFilters.length > 1 ? partnerContentTypeFilters : undefined}
          filterValue={partnerFilter}
          onFilterChange={setPartnerFilter}
          isLoading={isPartnerLoading}
          emptyState={
            <CarouselEmptyState
              icon={UserGroupIcon}
              iconBg='purple.50'
              iconColor='purple.500'
              title='No partner content yet'
              description='Connect with other public workspaces to share drills, plays, and articles across organizations.'
              actionLabel='Open partner settings'
              onAction={() => navigate(`/${workspaceId}/account/edit`)}
            />
          }
        >
          {visiblePartner.map((item) => (
            <ItemCardGrid key={item.id} {...cardProps(item)} />
          ))}
        </ContentCarousel>
      </Box>
      <ContentTypePickerDialog
        mode='create'
        isOpen={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
      <ContentTypePickerDialog
        mode='browse'
        isOpen={browseDialogOpen}
        onClose={() => setBrowseDialogOpen(false)}
      />
      <FindContentDialog
        isOpen={findDialogOpen}
        onClose={() => setFindDialogOpen(false)}
        onSelect={(item) => openContentDrawer(item, false)}
      />
      <ShareContentDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onPrint={handlePrint}
      />
      <ContentDrawer
        isOpen={contentDrawer.isOpen}
        onClose={contentDrawer.close}
        content={selectedContent}
        autoOpenPrintDialog={autoOpenPrintDialog}
      />
    </Box>
  );
}
